import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import "./BlogsScreen.css";
import Navbar from "./Navbar";
import Footer from "./Footer";

const BlogsScreen = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((blog) => blog.isActive === true);
        setBlogs(data);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  // Collect all unique tags from all blogs
  const allTags = [...new Set(blogs.flatMap((b) => b.tags || []))];

  // Filter blogs by search + tag
  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      searchTerm === "" ||
      blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.authorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.travellersQuote?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesTag =
      selectedTag === "" || (blog.tags || []).includes(selectedTag);
      
    return matchesSearch && matchesTag;
  });

  return (
    <div className="blogs-page-container">
      {/* Navbar Component */}
      <Navbar />

      {/* Page Hero */}
      <section className="blogs-hero">
        <div className="blogs-hero-overlay">
          <p className="blogs-hero-eyebrow">✈️ From Our Travelers</p>
          <h1>Travel Stories & Blogs</h1>
          <p>Real experiences, honest guides, and inspiring journeys from around the world.</p>
        </div>
      </section>

      {/* Search + Filter Bar */}
      <section className="blogs-filter-bar">
        <div className="blogs-search-wrap">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search blogs by title, author..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {allTags.length > 0 && (
          <div className="blogs-tags-filter">
            <button
              className={`filter-tag ${selectedTag === "" ? "active" : ""}`}
              onClick={() => setSelectedTag("")}
            >
              All
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                className={`filter-tag ${selectedTag === tag ? "active" : ""}`}
                onClick={() => setSelectedTag(selectedTag === tag ? "" : tag)}
              >
                #{tag}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Blogs Grid */}
      <section className="blogs-list-section">
        {loading ? (
          <div className="blogs-loading">
            <div className="blogs-spinner"></div>
            <p>Loading travel stories...</p>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="blogs-empty">
            <i className="fas fa-newspaper"></i>
            <h3>
              {searchTerm || selectedTag ? "No blogs match your search." : "No blogs published yet."}
            </h3>
            {(searchTerm || selectedTag) && (
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedTag("");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="blogs-count">
              {filteredBlogs.length} {filteredBlogs.length === 1 ? "story" : "stories"} found
            </p>
            <div className="blogs-all-grid">
              {filteredBlogs.map((blog, idx) => (
                <div
                  className={`blog-list-card ${idx === 0 && !searchTerm && !selectedTag ? "featured" : ""}`}
                  key={blog.id}
                  onClick={() => navigate(`/blogs/${blog.id}`)}
                >
                  {/* Cover Image */}
                  <div className="blog-list-image">
                    {blog.coverImageUrl ? (
                      <img src={blog.coverImageUrl} alt={blog.title} loading="lazy" />
                    ) : (
                      <div className="blog-list-img-placeholder">
                        <i className="fas fa-image"></i>
                      </div>
                    )}
                    {blog.createdAt && (
                      <span className="blog-list-date">{formatDate(blog.createdAt)}</span>
                    )}
                    {idx === 0 && !searchTerm && !selectedTag && (
                      <span className="featured-badge">⭐ Featured</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="blog-list-content">
                    {/* Tags */}
                    {blog.tags && blog.tags.length > 0 && (
                      <div className="blog-list-tags">
                        {blog.tags.slice(0, 3).map((tag, i) => (
                          <span
                            className={`blog-list-tag ${selectedTag === tag ? "tag-active" : ""}`}
                            key={i}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTag(selectedTag === tag ? "" : tag);
                            }}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Title */}
                    <h3 className="blog-list-title">{blog.title}</h3>

                    {/* Quote */}
                    {blog.travellersQuote && (
                      <p className="blog-list-quote">
                        <i className="fas fa-quote-left"></i> {blog.travellersQuote}
                      </p>
                    )}

                    {/* Body preview */}
                    {blog.body && (
                      <p className="blog-list-body-preview">
                        {blog.body.length > 120 ? blog.body.slice(0, 120) + "..." : blog.body}
                      </p>
                    )}

                    {/* Author + Read More row */}
                    <div className="blog-list-footer">
                      {blog.authorName ? (
                        <div className="blog-list-author">
                          <div className="blog-list-avatar">
                            {blog.authorName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="blog-list-author-name">{blog.authorName}</span>
                            {blog.authorDesignation && (
                              <span className="blog-list-author-role">{blog.authorDesignation}</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span />
                      )}
                      <button className="blog-list-read-btn">
                        Read More <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default BlogsScreen;